import { Infer, v } from 'convex/values'
import type { Doc } from '../_generated/dataModel'
import type { PostActionResult } from '../games/helpers/types'
import type { PlayerActionArg } from '../shared/types'
import type { AdvisorSupportedModeValue } from './constants'

export const advisorSupportedModeValidator = v.union(
  v.literal('practice'),
  v.literal('private'),
  v.literal('public'),
  v.literal('community'),
  v.literal('daily10')
)
export type AdvisorSupportedMode = AdvisorSupportedModeValue

export const advisorGradeValidator = v.union(v.literal('aligned'), v.literal('close'), v.literal('offProfile'))
export type AdvisorGrade = Infer<typeof advisorGradeValidator>
export type AdvisorTransitionContext = Infer<typeof advisorTransitionContextValidator>

export const advisorStreetValidator = v.union(
  v.literal('preflop'),
  v.literal('flop'),
  v.literal('turn'),
  v.literal('river')
)
export type AdvisorStreet = Infer<typeof advisorStreetValidator>

export const advisorConceptValidator = v.union(
  v.literal('sizing'),
  v.literal('potOdds'),
  v.literal('valueBetting'),
  v.literal('aggression'),
  v.literal('handStrength'),
  v.literal('drawing'),
  v.literal('position'),
  v.literal('bluffing')
)
export type AdvisorConcept = Infer<typeof advisorConceptValidator>

export const advisorDeviationValidator = v.union(
  v.literal('none'),
  v.literal('tooLarge'),
  v.literal('tooSmall'),
  v.literal('tooAggressive'),
  v.literal('tooPassive'),
  v.literal('foldedTooSoon'),
  v.literal('continuedTooLoose'),
  v.literal('missedValue'),
  // `differentAction` is the residual for a same-band bet-vs-raise mismatch
  // (different aggressive action types with comparable sizings). The engine
  // never offers `bet` and `raise` together, so this only arises from an
  // out-of-context action; `getAdvisorDeviation` throws for anything truly
  // impossible instead of silently mislabeling it.
  v.literal('differentAction')
)
export type AdvisorDeviation = Infer<typeof advisorDeviationValidator>

export const advisorImportanceValidator = v.union(v.literal('minor'), v.literal('meaningful'), v.literal('major'))
export type AdvisorImportance = Infer<typeof advisorImportanceValidator>

export const advisorActionTypeValidator = v.union(
  v.literal('fold'),
  v.literal('check'),
  v.literal('call'),
  v.literal('bet'),
  v.literal('raise'),
  v.literal('allin')
)
export type AdvisorActionType = Infer<typeof advisorActionTypeValidator>

export const advisorConversationKindValidator = v.union(
  v.literal('new'),
  v.literal('repeated'),
  v.literal('corrected'),
  v.literal('continued'),
  v.literal('reversed'),
  // 'advice' marks a PROACTIVE turn-start message (see `buildTurnAdvice`) —
  // created server-side, never produced by `classifyConversation`. 'confirmed'
  // marks the one follow-up grade after the player followed that advice.
  v.literal('advice'),
  v.literal('confirmed')
)
export type AdvisorConversationKind = Infer<typeof advisorConversationKindValidator>

export const advisorDecisionValidator = v.object({
  street: advisorStreetValidator,
  concept: advisorConceptValidator,
  deviation: advisorDeviationValidator,
  importance: advisorImportanceValidator,
  actualAction: advisorActionTypeValidator,
  recommendedAction: advisorActionTypeValidator,
  reasonKeys: v.array(v.string()),
  stateFactors: v.array(v.string()),
  copyVariantId: v.string(),
  // Whether the expert's aggressive line was value-driven or a bluff, derived
  // server-side from the driving conditions (and hand label as a fallback) so
  // consumers never have to pattern-match English `stateFactors` prose to
  // classify the player's aggression (see the app's fold-win recaps). Optional
  // because synthetic feedback (welcome bubble, win/loss recaps) omits it.
  aggressionMotivation: v.optional(v.union(v.literal('value'), v.literal('bluff'))),
  // The hero's starting-hand tier (strengthTier in poker-engine/utils, from the
  // 169-class preflop equity table). Set only for preflop spots, so consumers
  // can react to the hand itself (the app's premium-hand advice headline)
  // without parsing the hand label out of `stateFactors` prose. Optional
  // because postflop and synthetic feedback omit it.
  preflopTier: v.optional(v.union(v.literal('premium'), v.literal('strong'), v.literal('marginal'), v.literal('muck')))
})
export type AdvisorDecision = Infer<typeof advisorDecisionValidator>

export const advisorTransitionContextValidator = v.object({
  priorStreet: advisorStreetValidator,
  priorConcept: v.string(),
  priorDeviation: v.string(),
  priorAction: v.string(),
  priorRecommendedAction: v.string(),
  currentStreet: advisorStreetValidator,
  currentConcept: v.string(),
  currentAction: v.string(),
  currentRecommendedAction: v.string(),
  changedOn: advisorStreetValidator
})

export const advisorFeedbackValidator = v.object({
  grade: advisorGradeValidator,
  playerActionLabel: v.string(),
  recommendedActionLabel: v.string(),
  // Display contract: the UI shows exactly ONE supporting line —
  // `differenceReason` when present (grade !== 'aligned'), otherwise
  // `primaryReason`. They are never shown together, so `differenceReason`
  // must carry the full "why" on its own, not just name the adjustment.
  // Use `getAdvisorSupportingText` instead of reading these fields directly.
  primaryReason: v.string(),
  primaryReasonAlternative: v.optional(v.string()),
  differenceReason: v.optional(v.string()),
  differenceReasonAlternative: v.optional(v.string()),
  decision: advisorDecisionValidator,
  conversation: v.optional(
    v.object({
      kind: advisorConversationKindValidator,
      priorStreet: v.optional(advisorStreetValidator),
      transition: v.optional(advisorTransitionContextValidator),
      copyOffset: v.optional(v.number())
    })
  )
})
export type AdvisorFeedback = Infer<typeof advisorFeedbackValidator>

/**
 * The single supporting line a player actually sees for a piece of advisor
 * feedback. This is the ONE place that encodes the display rule; every
 * consumer (UI card, generated docs, tests) must go through it rather than
 * combining `primaryReason` and `differenceReason` themselves.
 *
 * Rule: when the player's action differs from the recommendation the
 * difference line replaces — not supplements — the primary reason, so it must
 * be a self-contained explanation.
 */
export function getAdvisorSupportingText(
  feedback: Pick<AdvisorFeedback, 'primaryReason' | 'differenceReason'>
): string {
  return feedback.differenceReason ?? feedback.primaryReason
}

export const advisorContextValidator = v.object({
  enabled: v.boolean(),
  supportedModes: v.array(advisorSupportedModeValidator)
})
export type AdvisorContext = Infer<typeof advisorContextValidator>

/** Pre-action state snapshot the mutation hands to the action wrapper so the
 * (CPU-heavy) advisor evaluation can run OUTSIDE the serialized mutation
 * transaction. The client never sees this — the action evaluates it and
 * returns only the resulting `advisorFeedback`. */
export type AdvisorEvaluationInput = {
  game: Doc<'games'>
  players: Doc<'players'>[]
  userId: string
  action: PlayerActionArg
}

export type ExecutePlayerActionInternalResult = PostActionResult & {
  /** Present when the advisor gating passed: the action wrapper evaluates this
   * snapshot (pure computation on pre-action state) and attaches the feedback. */
  advisorEvaluation?: AdvisorEvaluationInput
}

export type ExecutePlayerActionResponse = PostActionResult & {
  advisorFeedback?: AdvisorFeedback
}

/** Result of the proactive turn-start advice action. `spotKey` mirrors the
 * evaluation's rotation seed (`handNumber:actionsCount`) so the client can
 * discard advice that arrives after the spot has moved on. Null when the spot
 * is not one the advisor weighs in on. */
export type TurnAdviceResponse = {
  advice: AdvisorFeedback
  spotKey: string
} | null
